import { type ReactNode, useId } from "react";

// A switch, on or off at once (DESIGN.md, "Notifications": the profile's device switch and its kinds). Souvenir has
// none, so it's drawn here in Souvenir's own terms: a pill track on the paper's sunk tone that fills with the sun
// when on, its knob raised like the segmented control's thumb. A row of its own: its name and what it says on the
// left, the switch on the right, the whole row a 44 px target (its label activates it). A button with
// role="switch", named by its visible label and described by the lines under it. Off, a line under them can say what
// stops (`whenOff`).
export function SwitchRow({
  label,
  about,
  where,
  whenOff,
  checked,
  disabled = false,
  busy = false,
  onChange,
}: {
  label: ReactNode;
  about?: ReactNode;
  // Where what it turns on arrives ("Push and email"), said small under what it covers.
  where?: ReactNode;
  // What stops while it's off, said under the rest only then.
  whenOff?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  busy?: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = useId();
  const off = !checked && whenOff ? whenOff : null;
  const described = [about ? `${id}-about` : null, where ? `${id}-where` : null, off ? `${id}-off` : null]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={disabled ? "tm-switch-row is-disabled" : "tm-switch-row"}>
      <label htmlFor={id} className="tm-switch-row-text">
        <span id={`${id}-label`} className="callout tm-switch-label">
          {label}
        </span>
        {about && (
          <span id={`${id}-about`} className="caption tm-muted">
            {about}
          </span>
        )}
        {where && (
          <span id={`${id}-where`} className="caption tm-switch-where">
            {where}
          </span>
        )}
        {off && (
          <span id={`${id}-off`} className="caption tm-switch-off">
            {off}
          </span>
        )}
      </label>
      <button
        type="button"
        role="switch"
        id={id}
        className="tm-switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        {...(described ? { "aria-describedby": described } : {})}
        {...(busy ? { "aria-busy": true } : {})}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span className="tm-switch-knob" aria-hidden="true" />
      </button>
    </div>
  );
}
